import React, { useMemo } from 'react';
import { Box } from '@mui/material';
import { Vehicle, Sale } from '../../types';
import {
  buildContratoVendaParagraphs,
  buildContratoVendaVariables,
  chunkContratoParagraphsForPdf,
  ContratoParagraph,
} from './contratoVendaUtils';
import { CONTRATO_VENDA_TIMBRADO } from './contratoVendaConfig';

const pad = CONTRATO_VENDA_TIMBRADO.paddingScreen;
const PARAGRAPHS_PER_PAGE = 20;

interface ContratoVendaDocumentProps {
  vehicle: Vehicle;
  sale: Sale;
  dataEmissao?: string;
  /** Data URL (base64) do timbrado — obrigatório para exibir fundo corretamente */
  timbradoSrc: string;
}

const renderParagraph = (paragraph: ContratoParagraph, index: number) => {
  if (paragraph.kind === 'title') {
    return (
      <p key={index} className="titulo">
        {paragraph.text}
      </p>
    );
  }

  if (paragraph.kind === 'heading') {
    return (
      <p key={index} className="clausula clausula-heading">
        {paragraph.text}
      </p>
    );
  }

  if (paragraph.kind === 'signature') {
    return (
      <p key={index} className="clausula clausula-signature">
        {paragraph.text}
      </p>
    );
  }

  return (
    <p key={index} className="clausula">
      {paragraph.text}
    </p>
  );
};

const ContratoVendaDocument: React.FC<ContratoVendaDocumentProps> = ({
  vehicle,
  sale,
  dataEmissao = new Date().toISOString().split('T')[0],
  timbradoSrc,
}) => {
  const pages = useMemo(() => {
    const vars = buildContratoVendaVariables(vehicle, sale, dataEmissao);
    const paragraphs = buildContratoVendaParagraphs(vars);
    return chunkContratoParagraphsForPdf(paragraphs, PARAGRAPHS_PER_PAGE);
  }, [vehicle, sale, dataEmissao]);

  return (
    <Box className="contrato-print-root" sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
      {pages.map((chunk, pageIndex) => (
        <Box
          key={pageIndex}
          className="contrato-print-page"
          sx={{
            position: 'relative',
            width: '210mm',
            height: '297mm',
            overflow: 'hidden',
            boxSizing: 'border-box',
            bgcolor: '#fff',
            boxShadow: { xs: '0 2px 12px rgba(0,0,0,0.12)', print: 'none' },
            '@media print': {
              boxShadow: 'none',
              margin: 0,
              pageBreakAfter: pageIndex < pages.length - 1 ? 'always' : 'auto',
            },
          }}
        >
          <Box
            component="img"
            src={timbradoSrc}
            alt=""
            aria-hidden
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'fill',
              zIndex: 0,
              pointerEvents: 'none',
            }}
          />
          <Box
            className="contrato-document contrato-document-timbrado"
            sx={{
              position: 'relative',
              zIndex: 1,
              width: '100%',
              height: '100%',
              boxSizing: 'border-box',
              padding: `${pad.top}px ${pad.right}px ${pad.bottom}px ${pad.left}px`,
              fontFamily: '"Arial", "Helvetica", sans-serif',
              fontSize: 10,
              lineHeight: 1.45,
              color: '#111',
              '& .titulo': {
                textAlign: 'center',
                fontWeight: 'bold',
                fontSize: 12,
                marginBottom: 12,
                marginTop: 0,
                lineHeight: 1.25,
                textIndent: 0,
              },
              '& .clausula': {
                textAlign: 'justify',
                marginBottom: 6,
                textIndent: 22,
                marginTop: 0,
              },
              '& .clausula-heading': {
                textIndent: 0,
                fontWeight: 'bold',
                marginTop: 8,
              },
              '& .clausula-signature': {
                textIndent: 0,
                marginTop: 8,
                whiteSpace: 'pre-line',
              },
            }}
          >
            {chunk.map((paragraph, idx) => renderParagraph(paragraph, idx))}
            {pageIndex === pages.length - 1 && (
              <p style={{ marginTop: 8, fontSize: 8, color: '#444', textIndent: 0 }}>
                Documento gerado automaticamente. Recomenda-se revisão pelo setor jurídico antes do uso.
              </p>
            )}
          </Box>
        </Box>
      ))}
    </Box>
  );
};

export default ContratoVendaDocument;
